import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, FlexBox, getTokenCssVar, Icon, IconButton, useScreenClass } from '@vp/swan'
import { Logger } from '../../../utils/logger'
import { getCookie, setCookie } from '../../../utils/cookies'
import { getSelectCountryAndGeoIpText, redirectToDefaultCountry } from '../../../utils/country'
import CountrySelector from '../../common/country-selector'
import { COOKIE_KEYS, STORAGE_TTL } from '../../../constants/storage-keys'
import { getHeaderTracker } from '../../../tracking'
import { getRedirectURL, resolveUrlForTenant } from '../../../utils/page-availability'
import { useTenantConfig } from '../../../context/TenantConfigContext'
import { deriveSupportedCountries } from '../../../utils/tenant-geoip-data'
import { getCountryMeta } from '../../../constants/country-metadata'
import { useI18n } from '../../../context/TranslationContext'

interface GeoIpBarProps {
  locale: string
  tenant?: string
  currentCountry?: string
}

const tracker = {
  trackView(locale: string) {
    getHeaderTracker().trackFlyOutView('GeoIP Placement Fly-Out', `view_${locale}`)
  },
  trackOpen(country: string) {
    getHeaderTracker().trackFlyOutClick('GeoIP Placement Fly-Out', `dropdown_${country}`)
  },
  trackContinue(country: string) {
    getHeaderTracker().trackFlyOutClick('GeoIP Placement Fly-Out', `continue_${country}`)
  },
  trackClose(locale: string) {
    getHeaderTracker().trackFlyOutClick('GeoIP Placement Fly-Out', `close_${locale}`)
  },
}

export default function GeoIpBar({ locale, currentCountry, tenant }: Readonly<GeoIpBarProps>) {
  const screen = useScreenClass()
  const isMobile = screen === 'xs'
  const [showCountrySelector, setShowCountrySelector] = useState(false)
  const hideCountrySelector = () => setShowCountrySelector(false)

  const tenantConfig = useTenantConfig()

  const { showGeoIpBar, hideGeoIpBar } = useGeoIpBarVisibility(locale)

  // Use tenant-config-derived data when available, fall back to hardcoded VistaPrint data
  const { selectCountryText, geoIpBarText, countryText, continueText, countryUrl, preferredLocale } = useMemo(() => {
    if (tenantConfig && currentCountry) {
      // Derive the current site's country name from the page locale
      const currentSiteCountryCode = locale.split('-')[1]?.toUpperCase() ?? ''
      const currentSiteMeta = getCountryMeta(currentSiteCountryCode)
      const currentSiteCountryName = currentSiteMeta?.displayName ?? ''

      const countries = deriveSupportedCountries(tenantConfig, currentSiteCountryName)
      const matchedCountry = countries.find((c) => c.cloudflareCountryCode.toLowerCase() === currentCountry.toLowerCase())
      if (matchedCountry) {
        // Find the locale-specific alternate language if applicable
        const preferredLang = matchedCountry.alternateLanguages.find((lang) => lang.locale.toLowerCase() === locale.toLowerCase())
        return {
          selectCountryText: preferredLang?.selectCountryText || matchedCountry.selectCountryText,
          geoIpBarText: preferredLang?.geoIpBarText || matchedCountry.geoIpBarText || '',
          countryText: matchedCountry.text,
          continueText: preferredLang?.continueText || matchedCountry.continueText || 'Continue',
          countryUrl: resolveUrlForTenant(matchedCountry.url, tenant),
          preferredLocale: preferredLang?.locale || matchedCountry.locale,
        }
      }
    }
    // Fallback to hardcoded VistaPrint data
    return getSelectCountryAndGeoIpText(locale, currentCountry)
  }, [tenantConfig, currentCountry, locale])

  const handleContinue = useCallback(async () => {
    hideGeoIpBar()
    tracker.trackContinue(selectCountryText)

    const currentPath = globalThis.window ? globalThis.location.pathname : '/'
    const redirectPath = preferredLocale ? await getRedirectURL({ currentUrl: currentPath, requestLocale: preferredLocale, locale, tenant }) : '/'

    redirectToDefaultCountry(countryUrl + redirectPath + '?redirect=true&redirectOrigin=Suggested_' + countryText)
  }, [countryUrl, countryText, preferredLocale, locale, tenant, hideGeoIpBar])

  const handleClose = useCallback(() => {
    tracker.trackClose(locale)
    hideGeoIpBar()
    setCookie(COOKIE_KEYS.USER_CONFIRMED_MESSAGE, 'true', STORAGE_TTL.ONE_YEAR)
  }, [hideGeoIpBar])

  const handleSelectCountry = () => {
    tracker.trackOpen(selectCountryText)
    setShowCountrySelector(true)
  }

  useEffect(() => {
    if (showGeoIpBar && currentCountry) tracker.trackView(locale)
  }, [showGeoIpBar, locale, currentCountry])

  if (!showGeoIpBar || !currentCountry) {
    return null
  }

  return (
    <>
      <FlexBox
        justifyContent="center"
        fontSkin="body-small"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        textColor="standard"
        darkMode
        gap={4}
        p={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        style={{ backgroundColor: getTokenCssVar('SwanSemColorBgStrong'), position: 'relative' }}
      >
        <FlexBox m={0} fontSkin="body-small-bold" flexWrap="wrap" style={{ whiteSpace: 'pre-line' }}>
          {geoIpBarText}
        </FlexBox>

        <FlexBox justifyContent="space-between" gap={{ xs: '3', sm: '4' }} alignItems="center" style={{ width: isMobile ? '100%' : 'auto' }}>
          <CountrySelectorButton selectCountryText={selectCountryText} countryText={countryText!} isMobile={isMobile} onClick={handleSelectCountry} />
          <ContinueButton continueText={continueText} onClick={handleContinue} />
        </FlexBox>

        <CloseButton isMobile={isMobile} onClick={handleClose} />
      </FlexBox>
      {showCountrySelector && (
        <CountrySelector isOpen={showCountrySelector} handleClose={hideCountrySelector} locale={locale} tenant={tenant} selectedCountry={currentCountry} />
      )}
    </>
  )
}

function useGeoIpBarVisibility(locale?: string) {
  const [showGeoIpBar, setShowGeoIpBar] = useState(false)

  useEffect(() => {
    const isConfirmed = getCookie(COOKIE_KEYS.USER_CONFIRMED_MESSAGE)
    const countryFromCookie = getCookie(COOKIE_KEYS.CLOUDFLARE_COUNTRY)
    const currentSiteCountry = locale?.split('-')[1]?.toUpperCase() ?? ''
    const countryToCheck = countryFromCookie ?? currentSiteCountry
    const shouldShow = isConfirmed !== 'true' && countryToCheck !== 'US' && locale?.toLowerCase() === 'en-us'
    setShowGeoIpBar(shouldShow)

    Logger.instance.info(`user current country ${currentSiteCountry}`)
  }, [locale])

  const hideGeoIpBar = () => setShowGeoIpBar(false)

  return { showGeoIpBar, hideGeoIpBar }
}

interface CountrySelectorButtonProps {
  selectCountryText: string
  countryText: string
  isMobile: boolean
  onClick: () => void
}

function CountrySelectorButton({ selectCountryText, countryText, isMobile, onClick }: Readonly<CountrySelectorButtonProps>) {
  return (
    <Button
      skin="secondary"
      aria-label={`${selectCountryText} : ${countryText}`}
      textColor="standard"
      fontSkin="body-small"
      p={3}
      style={{
        minWidth: `calc(${getTokenCssVar('SwanSemSpace7')} * 5)`,
        width: isMobile ? '100%' : 'auto',
        height: getTokenCssVar('SwanSemSpace7'),
        justifyContent: 'space-between',
        flexGrow: 1,
      }}
      onClick={onClick}
    >
      {countryText}
      <Icon iconType="caretDown" size="small" ml={1} aria-hidden="true" />
    </Button>
  )
}

interface ContinueButtonProps {
  continueText: string
  onClick: () => void
}

function ContinueButton({ continueText, onClick }: Readonly<ContinueButtonProps>) {
  const buttonStyles = { height: getTokenCssVar('SwanSemSpace7'), minWidth: 'fit-content' }

  return (
    <Button skin="secondary" py={3} px={6} textColor="standard" fontSkin="body-small" style={buttonStyles} onClick={onClick}>
      {continueText}
    </Button>
  )
}

interface CloseButtonProps {
  isMobile: boolean
  onClick: () => void
}

function CloseButton({ isMobile, onClick }: Readonly<CloseButtonProps>) {
  const { translate } = useI18n()
  const mobileStyles = { position: 'absolute' as const, top: 0, right: 0 }

  return (
    <IconButton
      tooltip={translate('close')}
      skin="tertiary"
      p={3}
      textColor="standard"
      style={{ height: getTokenCssVar('SwanSemSpace7'), ...(isMobile ? mobileStyles : {}) }}
      onClick={onClick}
    >
      <Icon iconType="close" size="16p" />
    </IconButton>
  )
}
